import type { ChartSeriesData, ChartSeriesDataPoint, MeterData } from "@core/apis/shared";

/**
 * Sorts data points by datetime
 */
export const sortByDatetime = <TPoint extends { datetime: string }>(data: TPoint[]): TPoint[] => {
  return [...data].sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());
};

const COMPONENT_NAME_MAPPING: Record<string, string> = {
  // Standard mappings for chart compatibility
  heat_energy: "heat_energy",
  returntemp: "returntemp",
  supplytemp: "supplytemp",
  outdoor_temp: "outdoor_temp",
  radiator_system_supply_temp: "radiator_system_supply_temp",
  // API variations
  "heat energy": "heat_energy",
  return_temp: "returntemp",
  supply_temp: "supplytemp",
  "return temperature": "returntemp",
  "supply temperature": "supplytemp",
  return_temperature: "returntemp",
  supply_temperature: "supplytemp",
  radiator_supply_temp: "radiator_system_supply_temp",
};

/**
 * Formats raw meter data into a standardized format
 */
export const formatMeterComponentData = (component: MeterData): ChartSeriesData | null => {
  if (!component || !Array.isArray(component.data)) {
    return null;
  }

  const sortedData = sortByDatetime(component.data);

  // Normalize component name using mapping for chart compatibility
  const normalizedComponentName =
    COMPONENT_NAME_MAPPING[component.component] || component.component;

  return {
    ...component,
    component: normalizedComponentName,
    data: sortedData.map((point) => ({
      datetime: point.datetime,
      value: point.value,
      flags: [],
    })),
  };
};

/**
 * Formats sensor data into meter data format
 */
export const formatSensorData = (
  sensorData: any,
  sensorUids: string[],
  valueKey: string,
  metadata: {
    uid: string;
    meter: string;
    stage: string;
    component: string;
    variant: string;
  }
): ChartSeriesData | null => {
  if (!sensorData || sensorUids.length === 0) {
    return null;
  }

  const combinedData: ChartSeriesDataPoint[] = [];

  sensorUids.forEach((uid) => {
    const data = sensorData[uid];
    if (data && data.length > 0) {
      data.forEach((dataPoint: any) => {
        if (dataPoint.values[valueKey] !== undefined) {
          combinedData.push({
            datetime: dataPoint.timestamp,
            value: dataPoint.values[valueKey],
            flags: [],
          });
        }
      });
    }
  });

  if (combinedData.length === 0) {
    return null;
  }

  return {
    ...metadata,
    data: sortByDatetime(combinedData),
  };
};

/**
 * Format weather data
 */
export const formatWeatherData = (
  weatherData: any[],
  metadata: {
    uid: string;
    meter: string;
    stage: string;
    component: string;
    variant: string;
  }
): ChartSeriesData | null => {
  if (!weatherData || weatherData.length === 0) {
    return null;
  }

  return {
    ...metadata,
    data: weatherData.map((point) => ({
      datetime: point.datetime,
      value: point.value,
      flags: [],
    })),
  };
};

/**
 * Combine formats
 */
export const combineFormattedData = (
  ...dataSources: (ChartSeriesData | null)[]
): ChartSeriesData[] => {
  return dataSources.filter((data): data is ChartSeriesData => data !== null);
};
